import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Col, Layout, Menu, Popover, Row, Tooltip } from "antd";
import {
  User,
  Sun,
  Moon,
  Mail,
  ChevronLeft,
  ChevronsRight,
  Users,
  LogOut,
  Gift,
  PlusCircle,
  List,
  // Layers,
} from "lucide-react";
import moment from "moment";
import Logo from "../assets/svg/logos/logo.svg";
import LogoCollapsed from "../assets/svg/logos/logo-small.svg";
import LogoBlack from "../assets/svg/logos/logo-black.svg";
import NotificationDropdown from "./NotificationDropdown";
import { AuthContext } from "../context/AuthContext";
import { getSubscriptionUserInfo } from "../utils/utils";
import "../assets/css/settings.css";
import dayjs from "dayjs";
import { userRoles } from "../config";
import SearchData from "./SearchData";
import { ReactComponent as DashboardIcon } from "../assets/svg/sidebar/dashboard.svg";
import { ReactComponent as LeadsIcon } from "../assets/svg/sidebar/leads.svg";
import { ReactComponent as CalendarIcon } from "../assets/svg/sidebar/calendar.svg";
import { ReactComponent as AichatIcon } from "../assets/svg/sidebar/ai-chat.svg";
import { ReactComponent as CompaniesIcon } from "../assets/svg/sidebar/companies.svg";
import { ReactComponent as ExecutivesIcon } from "../assets/svg/sidebar/executives.svg";
import { ReactComponent as SearchIcon } from "../assets/svg/sidebar/search.svg";
import { ReactComponent as CampaignsIcon } from "../assets/svg/sidebar/campaigns.svg";
import { ReactComponent as ReputeIcon } from "../assets/svg/sidebar/repute.svg";
import { ReactComponent as SettingsIcon } from "../assets/svg/sidebar/settings.svg";
import { ReactComponent as WhatsAppIcon } from "../assets/svg/sidebar/whatsapp.svg";
import { ReactComponent as ChatIcon } from "../assets/svg/sidebar/ask-chat.svg";

const { Header, Sider, Content } = Layout;

const AuthenticatedLayout = (props) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLeadView,
    isCompanyView,
    isExecutiveView,
    logout,
    user,
    isLight,
    onToggleTheme,
    isRMView,
    isEmailSetupView,
    isWebToLeadView,
    isLeadCustomFieldView,
    isWhatsAppView,
    collapsed,
    onToggleSidebar,
  } = useContext(AuthContext);
  const isMobile = useMemo(() => window.innerWidth < 600, []);

  const toggleCollapse = () => {
    onToggleSidebar(!collapsed);
  };

  useEffect(() => {
    if (!isAuthenticated) navigate("/signin");
  }, [isAuthenticated, navigate]);

  const isItemActive = (itemKey) => {
    if (itemKey === location.pathname) return true;

    // Special handling for Data Search root which has Company and Executive
    if (
      itemKey === "/search-company-root" &&
      (location.pathname === "/search-company" ||
        location.pathname === "/search-executive")
    )
      return true;

    // Special handling for Campaigns root which has Email Setup, WhatsApp, Mail Merge and Audiences
    if (
      itemKey === "/campaigns" &&
      (location.pathname.startsWith("/whatsapp") ||
        location.pathname.startsWith("/campaigns/audience") ||
        location.pathname.startsWith("/mail-merge") ||
        location.pathname === "/settings/email-setup")
    )
      return true;

    if (
      itemKey === "/mail-merge" &&
      location.pathname.startsWith("/mail-merge")
    )
      return true;

    if (
      itemKey === "/campaigns/audience" &&
      location.pathname.startsWith("/campaigns/audience")
    )
      return true;

    if (
      itemKey === "/whatsapp-campaigns" &&
      location.pathname.startsWith("/whatsapp-campaigns") &&
      location.pathname !== "/whatsapp-campaigns/create"
    )
      return true;

    // Special handling for Settings root
    if (
      itemKey === "/settings" &&
      [
        "/settings/user-management",
        "/settings/web-to-lead",
        "/settings/email-setup",
        "/settings/lead-custom-fields",
        "/leads-timeline/availability",
      ].includes(location.pathname)
    )
      return true;

    // Special handling for Reputation Manager root
    if (
      itemKey === "/reputation-manager" &&
      location.pathname.startsWith("/reputation-manager")
    )
      return true;

    return false;
  };

  const renderMenuIcon = (IconComponent, pathKey, labelName) => {
    const isActive = isItemActive(pathKey);

    if (collapsed) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-[70px] tw-h-[60px] tw-py-1 tw-relative">
          <div
            className={`sidebar-icon-box tw-flex tw-items-center tw-justify-center tw-w-[32px] tw-h-[32px] tw-rounded-lg tw-transition-colors ${
              isActive
                ? isLight
                  ? "tw-bg-[#e7e3ff] tw-text-[#5d44ff]"
                  : "tw-bg-[#445a8e] tw-text-white"
                : isLight
                  ? "tw-text-[#414448] hover:tw-bg-gray-100"
                  : "tw-text-gray-400 hover:tw-bg-gray-700"
            }`}
          >
            <IconComponent className="tw-size-[15px]" />
          </div>
          <div
            className={`sidebar-label tw-text-[11px] tw-font-medium tw-mt-1 tw-transition-colors tw-leading-tight tw-text-center ${
              isActive
                ? isLight
                  ? "tw-text-[#5d44ff]"
                  : "tw-text-white"
                : isLight
                  ? "tw-text-[#414448]"
                  : "tw-text-gray-400"
            }`}
          >
            {labelName}
          </div>
        </div>
      );
    }

    // Expanded state
    return (
      <IconComponent
        className={`tw-size-[15px] tw-transition-colors ${
          isActive
            ? isLight
              ? "tw-text-[#5d44ff]"
              : "tw-text-white"
            : isLight
              ? "tw-text-[#414448]"
              : "tw-text-gray-400"
        }`}
      />
    );
  };

  const renderSubmenuIcon = (IconComponent, pathKey) => {
    const isActive = isItemActive(pathKey);
    return (
      <IconComponent
        className={`tw-size-[15px] tw-transition-colors ${
          isActive
            ? isLight
              ? "tw-text-[#5d44ff]"
              : "tw-text-white"
            : isLight
              ? "tw-text-[#414448]"
              : "tw-text-gray-400"
        }`}
      />
    );
  };

  const menuItemsCollapsed = [
    {
      key: "/dashboard-view",
      isVisible: true,
      icon: (
        <div
          className={`sidebar-item ${"/dashboard-view".includes(location.pathname) && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <DashboardIcon className="tw-size-[15px]" />
          {collapsed && <div className="sidebar-label">Dashboard</div>}
        </div>
      ),
      label: <NavLink to="/dashboard-view">Dashboard</NavLink>,
    },
    {
      key: "/search-lead",
      isVisible: isLeadView,
      icon: (
        <div
          className={`sidebar-item ${"/search-lead".includes(location.pathname) && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <LeadsIcon className="tw-size-[15px]" />
          {collapsed && <div className="sidebar-label">Leads</div>}
        </div>
      ),
      label: <NavLink to="/search-lead">Leads</NavLink>,
    },
    {
      type: "divider",
      isVisible: true,
    },
    {
      key: "/campaigns",
      isVisible: true,
      icon: (
        <div
          className={`sidebar-item ${isItemActive("/campaigns") && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <CampaignsIcon className="tw-size-[15px]" />
          {collapsed && <div className="sidebar-label">Campaigns</div>}
        </div>
      ),
      label: "Campaigns",
      children: [
        {
          key: "/mail-merge",
          isVisible: true,
          icon: <Mail className="tw-size-[15px]" />,
          label: <NavLink to="/mail-merge">Mail Merge</NavLink>,
        },
        {
          key: "/whatsapp",
          isVisible:
            (user?.role !== userRoles?.admin && isWhatsAppView) ||
            user?.role === userRoles?.admin,
          icon: <WhatsAppIcon className="tw-size-[15px]" />,
          label: <NavLink to="/whatsapp">WhatsApp</NavLink>,
        },
        {
          key: "/campaigns/audience",
          isVisible:
            (user?.role !== userRoles?.admin &&
              (isWhatsAppView || isEmailSetupView)) ||
            user?.role === userRoles?.admin,
          icon: <Users className="tw-size-[15px]" />,
          label: <NavLink to="/campaigns/audience">Audiences</NavLink>,
        },
        {
          key: "/whatsapp-campaigns/create",
          isVisible:
            (user?.role !== userRoles?.admin && isWhatsAppView) ||
            user?.role === userRoles?.admin,
          icon: <PlusCircle className="tw-size-[15px]" />,
          label: (
            <NavLink to="/whatsapp-campaigns/create">Create Campaign</NavLink>
          ),
        },
        {
          key: "/whatsapp-campaigns",
          isVisible:
            (user?.role !== userRoles?.admin && isWhatsAppView) ||
            user?.role === userRoles?.admin,
          icon: <List className="tw-size-[15px]" />,
          label: <NavLink to="/whatsapp-campaigns">Campaign List</NavLink>,
        },
      ].filter((item) => item.isVisible),
    },
    {
      key: "/leads-timeline",
      isVisible: true,
      icon: (
        <div
          className={`sidebar-item ${"/leads-timeline".includes(location.pathname) && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <NavLink to="/leads-timeline">
            <CalendarIcon className="tw-size-[15px]" />
          </NavLink>
          {collapsed && (
            <NavLink to="/leads-timeline">
              <div className="sidebar-label">Calendar</div>
            </NavLink>
          )}
        </div>
      ),
      label: <NavLink to="/leads-timeline">Calendar</NavLink>,
    },
    {
      type: "divider",
      isVisible: true,
    },
    {
      key: "/search-company",
      isVisible: isCompanyView || isExecutiveView,
      icon: (
        <div
          className={`sidebar-item ${("/search-company".includes(location.pathname) || "/search-executive".includes(location.pathname)) && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <NavLink to="/search-company">
            <div className={collapsed && "tw-mb-[10px] tw-mt-[15px]"}>
              <SearchIcon className="tw-size-[15px]" />
            </div>
          </NavLink>

          {collapsed && (
            <NavLink to="/search-company">
              <div className="sidebar-label">Data Search</div>{" "}
            </NavLink>
          )}
        </div>
      ),
      label: "Data Search",
      children: [
        {
          key: "/search-company",
          isVisible: isCompanyView,
          icon: <CompaniesIcon className="tw-size-[15px] ml-[20px]" />,
          label: <NavLink to="/search-company">Company</NavLink>,
        },
        {
          key: "/search-executive",
          isVisible: isExecutiveView,
          icon: <ExecutivesIcon className="tw-size-[15px]" />,
          label: <NavLink to="/search-executive">Executive</NavLink>,
        },
      ],
    },
    {
      key: "/gen-ai-widget",
      isVisible: true,
      className: collapsed ? "" : "!tw-bg-theme !tw-text-white",
      icon: (
        <div
          className={`sidebar-item ${collapsed ? "tw-backdrop-blur-[6.7px] tw-bg-theme !tw-rounded-full !tw-text-white" : ""}`}
        >
          <AichatIcon className="tw-size-[15px]" />

          {collapsed && (
            <div className={`sidebar-label ${isLight && "!tw-text-theme"}`}>
              BeyondAI
            </div>
          )}
        </div>
      ),
      label: (
        <NavLink
          to="/gen-ai-widget"
          className="tw-flex tw-items-center !tw-text-white tw-justify-between"
        >
          Ask BeyondAI <ChatIcon />
        </NavLink>
      ),
    },
    {
      key: "/reputation-manager",
      isVisible:
        (user?.role !== userRoles?.admin && isRMView) ||
        user?.role === userRoles?.admin,
      icon: (
        <div
          className={`sidebar-item ${["/reputation-manager/reviews"].includes(location.pathname) && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <NavLink to="/reputation-manager/reviews">
            <ReputeIcon className="tw-size-[15px]" />
          </NavLink>
          {collapsed && (
            <NavLink to="/reputation-manager/reviews">
              <div className="sidebar-label">Repute</div>
            </NavLink>
          )}
        </div>
      ),
      label: <NavLink to="/reputation-manager/reviews">Repute</NavLink>,
    },
  ];

  const settingsMenuItemCollapsed = [
    {
      key: "/settings",
      isVisible: true,
      icon: (
        <div
          className={`sidebar-item ${["/settings/user-management", "/settings/web-to-lead", "/settings/email-setup", "/settings/lead-custom-fields", "/leads-timeline/availability"].includes(location.pathname) && collapsed ? (isLight ? "tw-bg-[#efedff] !tw-text-[#0b0055]" : "tw-bg-[#445a8e]") : ""}`}
        >
          <SettingsIcon
            className={`tw-size-[15px] ${isLight ? " !tw-text-text-primary" : " !tw-text-white"}`}
          />
          {collapsed && <div className="sidebar-label">Settings</div>}
        </div>
      ),
      label: <NavLink to="/settings">Settings</NavLink>,
    },
  ];

  const menuItems = [
    {
      type: "group",
      label: !collapsed && (
        <span className="tw-text-[#9CA3AF] tw-text-[10px] tw-font-medium tw-tracking-[0.8px] tw-leading-[15px] tw-mb-2 tw-block">
          OVERVIEW
        </span>
      ),
      children: [
        {
          key: "/dashboard-view",
          isVisible: true,
          icon: renderMenuIcon(DashboardIcon, "/dashboard-view", "Dashboard"),
          label: <NavLink to="/dashboard-view">Dashboard</NavLink>,
        },
        {
          key: "/search-lead",
          isVisible: isLeadView,
          icon: renderMenuIcon(LeadsIcon, "/search-lead", "Leads"),
          label: <NavLink to="/search-lead">Leads</NavLink>,
        },
      ].filter((item) => item.isVisible),
    },
    {
      type: "group",
      label: !collapsed && (
        <span className="tw-text-[#9CA3AF] tw-text-[10px] tw-font-medium tw-tracking-[0.8px] tw-leading-[15px] tw-mb-2 tw-block">
          ENGAGEMENT
        </span>
      ),
      children: [
        {
          key: "/campaigns",
          isVisible: true,
          icon: renderMenuIcon(CampaignsIcon, "/campaigns", "Campaigns"),
          label: "Campaigns",
          children: [
            {
              key: "/mail-merge",
              isVisible: true,
              icon: renderSubmenuIcon(Mail, "/mail-merge"),
              label: <NavLink to="/mail-merge">Mail Merge</NavLink>,
            },
            {
              key: "/settings/email-setup",
              isVisible:
                (user?.role !== userRoles?.admin && isEmailSetupView) ||
                user?.role === userRoles?.admin,
              icon: renderSubmenuIcon(Mail, "/settings/email-setup"),
              label: (
                <NavLink to="/settings/email-setup">Email Campaigns</NavLink>
              ),
            },
            {
              key: "/whatsapp",
              isVisible:
                (user?.role !== userRoles?.admin && isWhatsAppView) ||
                user?.role === userRoles?.admin,
              icon: renderSubmenuIcon(WhatsAppIcon, "/whatsapp"),
              label: <NavLink to="/whatsapp">WhatsApp</NavLink>,
            },
            {
              key: "/campaigns/audience",
              isVisible:
                (user?.role !== userRoles?.admin &&
                  (isWhatsAppView || isEmailSetupView)) ||
                user?.role === userRoles?.admin,
              icon: renderSubmenuIcon(Users, "/campaigns/audience"),
              label: <NavLink to="/campaigns/audience">Audiences</NavLink>,
            },
            {
              key: "/whatsapp-campaigns/create",
              isVisible:
                (user?.role !== userRoles?.admin && isWhatsAppView) ||
                user?.role === userRoles?.admin,
              icon: renderSubmenuIcon(PlusCircle, "/whatsapp-campaigns/create"),
              label: (
                <NavLink to="/whatsapp-campaigns/create">
                  Create Campaign
                </NavLink>
              ),
            },
            {
              key: "/whatsapp-campaigns",
              isVisible:
                (user?.role !== userRoles?.admin && isWhatsAppView) ||
                user?.role === userRoles?.admin,
              icon: renderSubmenuIcon(List, "/whatsapp-campaigns"),
              label: <NavLink to="/whatsapp-campaigns">Campaign List</NavLink>,
            },
          ].filter((item) => item.isVisible),
        },
        {
          key: "/leads-timeline",
          isVisible: true,
          icon: renderMenuIcon(CalendarIcon, "/leads-timeline", "Calendar"),
          label: <NavLink to="/leads-timeline">Calendar</NavLink>,
        },
      ].filter((item) => item.isVisible),
    },
    {
      type: "group",
      label: !collapsed && (
        <span className="tw-text-[#9CA3AF] tw-text-[10px] tw-font-medium tw-tracking-[0.8px] tw-leading-[15px] tw-mb-2 tw-block">
          INTELLIGENCE
        </span>
      ),
      children: [
        {
          key: "/search-company-root",
          isVisible: isCompanyView || isExecutiveView,
          icon: renderMenuIcon(SearchIcon, "/search-company-root", "Search"),
          label: "Data Search",
          children: [
            {
              key: "/search-company",
              isVisible: isCompanyView,
              icon: renderSubmenuIcon(CompaniesIcon, "/search-company"),
              label: <NavLink to="/search-company">Company</NavLink>,
            },
            {
              key: "/search-executive",
              isVisible: isExecutiveView,
              icon: renderSubmenuIcon(ExecutivesIcon, "/search-executive"),
              label: <NavLink to="/search-executive">Executive</NavLink>,
            },
          ].filter((item) => item.isVisible),
        },
        {
          key: "/gen-ai-widget",
          isVisible: true,
          icon: renderMenuIcon(AichatIcon, "/gen-ai-widget", "Beyond AI"),
          label: <NavLink to="/gen-ai-widget">Beyond AI</NavLink>,
        },
        {
          key: "/reputation-manager",
          isVisible:
            (user?.role !== userRoles?.admin && isRMView) ||
            user?.role === userRoles?.admin,
          icon: renderMenuIcon(ReputeIcon, "/reputation-manager", "Repute"),
          label: <NavLink to="/reputation-manager/reviews">Repute</NavLink>,
        },
      ].filter((item) => item.isVisible),
    },
    {
      type: "group",
      label: !collapsed && (
        <span className="tw-text-[#9CA3AF] tw-text-[10px] tw-font-medium tw-tracking-[0.8px] tw-leading-[15px] tw-mb-2 tw-block">
          SYSTEM
        </span>
      ),
      className: "system-menu-group",
      children: [
        {
          key: "/settings",
          isVisible: true,
          icon: renderMenuIcon(SettingsIcon, "/settings", "Settings"),
          label: <NavLink to="/settings">Settings</NavLink>,
        },
      ].filter((item) => item.isVisible),
    },
  ];

  const subscription = getSubscriptionUserInfo();
  const getDaysLeft = (futureDate) => {
    const today = dayjs().startOf("day");
    const target = dayjs(futureDate).startOf("day");
    return target.diff(today, "day");
  };
  const isToday = dayjs(subscription?.validTo).isSame(dayjs(), "day");
  const daysLeft = getDaysLeft(subscription?.validTo);

  const content = (
    <div className="!tw-font-inter tw-w-60 tw-p-0">
      {/* User Header Block */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-bg-slate-50/80 tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-mb-1.5">
        <div className="!tw-w-9 !tw-h-9 !tw-rounded-full !tw-bg-[#5D44FF] !tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-xs tw-shrink-0 tw-shadow-sm uppercase">
          {user?.firstName?.[0]} {user?.lastName?.[0]}
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-font-bold tw-text-xs tw-text-slate-800 tw-truncate tw-leading-snug">
            {user?.firstName} {user?.lastName}
          </div>
          <div className="tw-text-[11px] tw-text-slate-500 tw-truncate tw-mt-0.5 tw-font-normal">
            {user?.email}
          </div>
        </div>
      </div>

      {/* Menu Options */}
      <div className="tw-flex tw-flex-col tw-gap-0.5">
        <div
          className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-indigo-50/70 hover:tw-text-[#5D44FF] tw-cursor-pointer tw-transition-colors"
          onClick={() => {
            navigate("/my-account");
            setOpen(false);
          }}
        >
          <User
            className="tw-size-[16px] text-color-717171"
            style={{ marginRight: 7 }}
          />
          My Account
        </div>
        {/* Workspace Menu Option (Commented for future use)
        <div
          className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-indigo-50/70 hover:tw-text-[#5D44FF] tw-cursor-pointer tw-transition-colors"
          onClick={() => {
            navigate("/my-workspace");
            setOpen(false);
          }}
        >
          <Layers
            className="tw-size-[16px] text-color-717171"
            style={{ marginRight: 7 }}
          />
          Workspace
        </div>
        */}
        {daysLeft < 8 && (
          <div className="tw-my-1 tw-p-2.5 tw-rounded-lg tw-bg-amber-50/70 tw-border tw-border-solid tw-border-amber-200/60 tw-text-[11px] tw-font-medium tw-text-amber-800 tw-flex tw-flex-col tw-gap-2">
            <div>
              {isToday
                ? "Your free trial expires today. Subscribe now to avoid interruption."
                : daysLeft < 8 && daysLeft > -1
                  ? `You have ${daysLeft} days remaining in your free trial`
                  : "Your subscription is expired."}
            </div>
            <Button
              type="primary"
              className="theme-btn !tw-h-7 !tw-text-xs !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2]"
              onClick={() => navigate("/pricing")}
            >
              Subscribe Now
            </Button>
          </div>
        )}

        <div
          className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-slate-700 hover:tw-bg-rose-50 hover:tw-text-rose-600 tw-cursor-pointer tw-transition-colors"
          onClick={logout}
        >
          <LogOut
            className="tw-size-[16px] tw-text-[#717171]"
            style={{ marginRight: 7 }}
          />
          Sign Out
        </div>
      </div>
    </div>
  );

  const isChatPage =
    location.pathname.startsWith("/whatsapp") ||
    location.pathname === "/gen-ai-widget";

  const getSelectedMenuKey = (pathname) => {
    if (pathname.startsWith("/mail-merge")) {
      return "/mail-merge";
    }
    if (pathname.startsWith("/campaigns/audience")) {
      return "/campaigns/audience";
    }
    if (pathname === "/whatsapp-campaigns/create") {
      return "/whatsapp-campaigns/create";
    }
    if (
      pathname === "/whatsapp-campaigns" ||
      pathname.startsWith("/whatsapp-campaigns/")
    ) {
      return "/whatsapp-campaigns";
    }
    if (pathname.startsWith("/whatsapp")) {
      return "/whatsapp";
    }
    if (
      pathname.startsWith("/settings") ||
      pathname === "/leads-timeline/availability"
    ) {
      return "/settings";
    }
    return pathname;
  };

  return (
    <Layout
      className={`${isLight ? "light" : ""} !tw-font-inter ${isChatPage ? "tw-h-screen tw-overflow-hidden" : ""}`}
    >
      <Sider
        className={`layout-sidebar ${collapsed ? "!tw-p-1" : "!tw-p-0"} !tw-font-inter tw-fixed tw-left-0 tw-top-0 tw-bottom-0 tw-overflow-auto tw-h-full ${isLight ? "tw-bg-white" : "!tw-bg-[#1f2937]"} ${collapsed ? "collapsed" : "not-collapsed"}`}
        collapsed={collapsed}
        onCollapse={onToggleSidebar}
        onBreakpoint={(broken) => onToggleSidebar(broken)}
        width={250}
        collapsedWidth={100}
      >
        {collapsed ? (
          <div className="tw-flex tw-flex-col tw-justify-between tw-h-full">
            <div>
              <div className="tw-flex tw-items-center tw-justify-center tw-mt-4 tw-mb-6 tw-mx-3">
                <NavLink to="/dashboard-view">
                  <img
                    src={LogoCollapsed}
                    alt=""
                    className="tw-w-[18px] tw-h-[22px]"
                  />
                </NavLink>
              </div>
              <div
                className={`tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-mx-[2px] ${isLight ? "tw-border-[#c7c7cc]" : "tw-border-gray-700"} tw-mb-6`}
              />

              <Menu
                mode="inline"
                selectedKeys={[getSelectedMenuKey(location.pathname)]}
                style={{ height: "calc(100%- 50px)", borderRight: 0 }}
                items={menuItemsCollapsed
                  ?.filter((d) => !!d?.isVisible)
                  ?.map((d) => {
                    const { isVisible, ...rest } = d;
                    return rest?.children?.length
                      ? {
                          ...rest,
                          children: rest.children
                            .filter((i) => !!i?.isVisible)
                            .map(
                              ({ isVisible: childIsVisible, ...childRest }) =>
                                childRest,
                            ),
                        }
                      : rest;
                  })}
                className="layout-menu !tw-font-inter"
              />
            </div>
            <div>
              <Menu
                mode="inline"
                selectedKeys={[getSelectedMenuKey(location.pathname)]}
                items={settingsMenuItemCollapsed
                  ?.filter((d) => !!d?.isVisible)
                  ?.map((d) => {
                    const { isVisible, ...rest } = d;
                    return rest?.children?.length
                      ? {
                          ...rest,
                          children: rest.children
                            .filter((i) => !!i?.isVisible)
                            .map(
                              ({ isVisible: childIsVisible, ...childRest }) =>
                                childRest,
                            ),
                        }
                      : rest;
                  })}
                className="layout-menu text-white border-none !tw-font-inter"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="tw-h-[64px] tw-px-3 tw-flex tw-items-center tw-justify-start">
              <NavLink to="/dashboard-view">
                <img
                  src={isLight ? LogoBlack : Logo}
                  alt=""
                  className="tw-h-[24px]"
                />
              </NavLink>
            </div>
            <div className="tw-flex tw-justify-end tw-px-3 tw-mb-2">
              <div className="tw-cursor-pointer tw-text-xl tw-flex tw-items-center">
                {isLight ? (
                  <Sun
                    className="tw-size-[18px] tw-text-black hover:tw-text-[#5d44ff] tw-transition-colors"
                    onClick={() => onToggleTheme(false)}
                  />
                ) : (
                  <Moon
                    className="tw-size-[18px] tw-text-white hover:tw-text-[#5d44ff] tw-transition-colors"
                    onClick={() => onToggleTheme(true)}
                  />
                )}
              </div>
            </div>

            <Menu
              mode="inline"
              selectedKeys={[getSelectedMenuKey(location.pathname)]}
              style={{ borderRight: 0, background: "transparent" }}
              items={menuItems
                ?.map((group) => {
                  const visibleChildren = group.children?.filter(
                    (item) => !!item.isVisible,
                  );
                  if (!visibleChildren || visibleChildren.length === 0)
                    return null;

                  const { isVisible, ...groupRest } = group;

                  return {
                    ...groupRest,
                    children: visibleChildren.map((item) => {
                      const { isVisible: itemIsVisible, ...itemRest } = item;
                      if (itemRest.children) {
                        return {
                          ...itemRest,
                          children: itemRest.children
                            .filter((sub) => !!sub.isVisible)
                            .map(
                              ({ isVisible: subIsVisible, ...subRest }) =>
                                subRest,
                            ),
                        };
                      }
                      return itemRest;
                    }),
                  };
                })
                ?.filter(Boolean)}
              className="layout-menu !tw-font-inter"
            />
          </>
        )}
      </Sider>

      <Layout
        style={{
          marginLeft: collapsed ? 100 : 250,
          transition: "margin-left 0.2s",
        }}
        className={`!tw-font-inter tw-relative ${isChatPage ? "tw-h-screen tw-flex tw-flex-col" : ""}`}
      >
        <Header
          className={`layout-header tw-flex tw-items-center tw-justify-between gap-20 !tw-font-inter !tw-z-[1001] tw-border-none ${isChatPage ? "tw-shadow-[0_2px_10px_rgba(0,0,0,0.04)]" : "!tw-shadow-none"}`}
        >
          <div
            className={`tw-absolute ${collapsed ? "tw-left-[-18px] !tw-size-[36px]" : "tw-left-[-14px] tw-top-[18px] !tw-size-[28px] tw-border tw-border-[#c7c7cc]"} tw-bg-white tw-shadow-[0px_0px_3.1px_0px_#00000040] tw-rounded-full tw-flex tw-justify-center tw-items-center tw-cursor-pointer ${collapsed ? "" : "tw-text-xs"} !tw-z-[1050]`}
            onClick={toggleCollapse}
          >
            {collapsed ? (
              <ChevronsRight className="tw-size-[16px]" />
            ) : (
              <ChevronLeft className="tw-size-[16px]" />
            )}
          </div>
          <Row className="tw-justify-between tw-w-full">
            <Col md={5}></Col>

            {!isMobile && (
              <Col md={11} className="tw-flex tw-justify-center !tw-font-inter">
                <div className="search-advance">
                  <SearchData />
                </div>
              </Col>
            )}
            <Col
              md={8}
              className="tw-flex tw-items-center !tw-font-inter tw-justify-end tw-gap-[15px]"
            >
              {!isMobile && (
                <div className="tw-font-bold !tw-font-inter tw-text-sm tw-text-[#65686F] tw-pr-4">
                  {moment().format("dddd, D MMM YYYY")}
                </div>
              )}
              <NotificationDropdown isAuthLayout />

              {/* Workspace Header Shortcut (Commented for future use)
              <Tooltip title="Workspace">
                <Badge dot offset={[-2, 2]}>
                  <div
                    onClick={() => navigate("/my-workspace")}
                    className={`tw-w-9 tw-h-9 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors ${
                      location.pathname === "/my-workspace"
                        ? "tw-bg-[#5D44FF] tw-text-white tw-shadow-sm"
                        : "tw-bg-indigo-50 tw-text-[#5D44FF] tw-border tw-border-solid tw-border-indigo-100 hover:tw-bg-indigo-100"
                    }`}
                  >
                    <Layers className="tw-w-4 tw-h-4" />
                  </div>
                </Badge>
              </Tooltip>
              */}

              <Popover
                content={content}
                trigger="click"
                open={open}
                onOpenChange={(visible) => setOpen(visible)}
                overlayClassName="profile-menu-popover"
              >
                <Tooltip title="My Account">
                  <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-text-[#5D44FF] tw-border tw-border-solid tw-border-indigo-100 hover:tw-bg-indigo-100 tw-transition-colors tw-cursor-pointer">
                    <User className="tw-w-5 tw-h-5 tw-text-[#5D44FF]" />
                  </div>
                </Tooltip>
              </Popover>
            </Col>
          </Row>
        </Header>

        <Content
          className={`layout-container ${isChatPage ? "tw-flex-1 tw-min-h-0 tw-overflow-hidden tw-flex tw-flex-col" : ""}`}
        >
          {props?.children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AuthenticatedLayout;
